<!-- eslint-disable vue/valid-v-slot -->

<template>
  <div class="licenses-page">
    <div class="page-header">
      <div class="page-header-text">
        <h1 class="page-title">Licencje TAXI</h1>
        <p class="page-subtitle">
          Numery licencji przewozowych firmy. Kreator dokumentów urzędowych
          podstawia je sam po wybraniu miasta.
        </p>
      </div>
      <button class="action-pill action-pill--primary" @click="otworzNowa">
        <v-icon size="14">mdi-plus</v-icon>Dodaj licencję
      </button>
    </div>

    <v-alert v-if="wygasajace.length" type="warning" text dense class="mb-4">
      <strong>{{ wygasajace.length }}</strong>
      {{ wygasajace.length === 1 ? 'licencja kończy się' : 'licencje kończą się' }}
      w ciągu 90 dni:
      {{ wygasajace.map((l) => `${l.city} (${dataPl(l.expiresAt)})`).join(', ') }}
    </v-alert>

    <v-data-table
      :headers="headers"
      :items="licencje"
      :loading="ladowanie"
      :search="szukaj"
      class="elevation-1"
      no-data-text="Brak licencji. Dodaj pierwszą, żeby kreator mógł ją podstawiać."
    >
      <template v-slot:top>
        <div class="table-section-title">
          <div class="filter-icon">
            <v-icon size="18" color="#5478FF">mdi-certificate-outline</v-icon>
          </div>
          <div class="filter-title">Licencje przewozowe</div>
        </div>
        <div class="search-row">
          <div class="search-input">
            <v-icon size="18" color="#6E7691">mdi-magnify</v-icon>
            <input v-model="szukaj" type="text" placeholder="Szukaj po mieście lub numerze" />
          </div>
        </div>
      </template>

      <template v-slot:item.issuedAt="{ item }">{{ dataPl(item.issuedAt) }}</template>

      <template v-slot:item.expiresAt="{ item }">
        <span v-if="!item.expiresAt" class="bezterminowa">bezterminowa</span>
        <v-chip v-else small :color="kolorTerminu(item.expiresAt)" dark>
          {{ dataPl(item.expiresAt) }}
        </v-chip>
      </template>

      <template v-slot:item.edytuj="{ item }">
        <v-btn class="warning" @click="otworzEdycje(item)">
          <v-icon small class="pt-1">mdi-pencil</v-icon>
        </v-btn>
      </template>

      <template v-slot:item.usun="{ item }">
        <v-btn class="error" @click="usun(item)">
          <v-icon small class="pt-1">mdi-delete</v-icon>
        </v-btn>
      </template>
    </v-data-table>

    <v-dialog content-class="dark-form-dialog" v-model="dialog" max-width="620px" persistent>
      <v-card>
        <v-card-title>
          <span class="headline">{{ formularz.id ? 'Edycja licencji' : 'Nowa licencja' }}</span>
        </v-card-title>
        <v-card-text>
          <v-combobox
            v-model="formularz.city"
            :items="miasta"
            label="Miasto *"
            hint="Nazwa musi być taka sama jak w kreatorze — wybierz z listy"
            persistent-hint
            outlined
            dense
            class="mb-3"
          />
          <v-text-field
            v-model="formularz.licenseNumber"
            label="Numer licencji *"
            placeholder="np. 1234/2026"
            outlined
            dense
            class="mb-3"
          />
          <v-row dense>
            <v-col cols="12" sm="6">
              <v-text-field
                v-model="formularz.issuedAt"
                label="Data wydania"
                type="date"
                outlined
                dense
              />
            </v-col>
            <v-col cols="12" sm="6">
              <v-text-field
                v-model="formularz.expiresAt"
                label="Data końca"
                type="date"
                hint="Pusto = bezterminowa"
                persistent-hint
                outlined
                dense
              />
            </v-col>
          </v-row>
          <v-text-field
            v-model="formularz.authority"
            label="Organ wydający"
            placeholder="np. Prezydent Miasta Torunia"
            outlined
            dense
            class="mt-3 mb-3"
          />
          <v-textarea v-model="formularz.note" label="Notatka" rows="2" outlined dense />
          <v-alert v-if="blad" type="error" text dense class="mt-2 mb-0">{{ blad }}</v-alert>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn text @click="dialog = false">Anuluj</v-btn>
          <v-btn color="primary" :loading="zapisywanie" @click="zapisz">Zapisz</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import axios from 'axios';
import Swal from 'sweetalert2';

const API = process.env.VUE_APP_ROOT_API;

// Licencja kończąca się w tylu dniach zapala ostrzeżenie na górze strony.
// Urzędy każą składać wniosek o przedłużenie z wyprzedzeniem, a licencja po
// terminie unieważnia wnioski, w których jej numer został użyty.
const PROG_OSTRZEZENIA_DNI = 90;

export default {
  name: 'CompanyLicensesPage',
  data() {
    return {
      licencje: [],
      miasta: [],
      ladowanie: false,
      zapisywanie: false,
      szukaj: '',
      dialog: false,
      blad: '',
      formularz: this.pustyFormularz(),
      headers: [
        { text: 'Miasto', value: 'city' },
        { text: 'Numer licencji', value: 'licenseNumber' },
        { text: 'Wydana', value: 'issuedAt' },
        { text: 'Ważna do', value: 'expiresAt' },
        { text: 'Organ wydający', value: 'authority' },
        { text: '', value: 'edytuj', sortable: false, width: 60 },
        { text: '', value: 'usun', sortable: false, width: 60 },
      ],
    };
  },
  computed: {
    wygasajace() {
      const granica = new Date();
      granica.setDate(granica.getDate() + PROG_OSTRZEZENIA_DNI);
      return this.licencje.filter(
        (l) => l.expiresAt && new Date(l.expiresAt) <= granica,
      );
    },
  },
  created() {
    this.pobierz();
    this.pobierzMiasta();
  },
  methods: {
    naglowki() {
      return {
        Authorization: `Bearer ${localStorage.getItem('token')}`,
        'x-current-page': 'company-licenses',
      };
    },
    pustyFormularz() {
      return {
        id: null,
        city: '',
        licenseNumber: '',
        issuedAt: '',
        expiresAt: '',
        authority: '',
        note: '',
      };
    },
    dataPl(wartosc) {
      if (!wartosc) return '—';
      return new Date(wartosc).toLocaleDateString('pl-PL');
    },
    kolorTerminu(expiresAt) {
      const koniec = new Date(expiresAt);
      const dzis = new Date();
      if (koniec < dzis) return 'red darken-2';
      const granica = new Date();
      granica.setDate(granica.getDate() + PROG_OSTRZEZENIA_DNI);
      return koniec <= granica ? 'orange darken-2' : 'green darken-2';
    },
    async pobierz() {
      this.ladowanie = true;
      try {
        const { data } = await axios.get(`${API}company-licenses`, {
          headers: this.naglowki(),
        });
        this.licencje = data.data || [];
      } catch (e) {
        Swal.fire('Nie udało się pobrać licencji', '', 'error');
      } finally {
        this.ladowanie = false;
      }
    },
    // Miasta bierzemy z tego samego katalogu urzędów, z którego korzysta
    // kreator — inaczej literówka w nazwie rozjechałaby podstawianie numeru.
    async pobierzMiasta() {
      try {
        const { data } = await axios.get(
          `${API}document-generator/official-forms/catalog`,
          { headers: this.naglowki() },
        );
        const wpisy = (data && data.data && data.data.offices) || data.data || [];
        this.miasta = wpisy.map((o) => o.city).filter(Boolean).sort();
      } catch (e) {
        this.miasta = [];
      }
    },
    otworzNowa() {
      this.formularz = this.pustyFormularz();
      this.blad = '';
      this.dialog = true;
    },
    otworzEdycje(item) {
      this.formularz = {
        id: item.id,
        city: item.city,
        licenseNumber: item.licenseNumber,
        issuedAt: item.issuedAt ? item.issuedAt.slice(0, 10) : '',
        expiresAt: item.expiresAt ? item.expiresAt.slice(0, 10) : '',
        authority: item.authority || '',
        note: item.note || '',
      };
      this.blad = '';
      this.dialog = true;
    },
    async zapisz() {
      this.blad = '';
      if (!this.formularz.city || !String(this.formularz.city).trim()) {
        this.blad = 'Podaj miasto.';
        return;
      }
      if (!this.formularz.licenseNumber || !this.formularz.licenseNumber.trim()) {
        this.blad = 'Podaj numer licencji.';
        return;
      }
      this.zapisywanie = true;
      try {
        await axios.post(
          `${API}company-licenses`,
          {
            id: this.formularz.id || undefined,
            city: String(this.formularz.city).trim(),
            licenseNumber: this.formularz.licenseNumber.trim(),
            issuedAt: this.formularz.issuedAt || undefined,
            expiresAt: this.formularz.expiresAt || undefined,
            authority: this.formularz.authority || undefined,
            note: this.formularz.note || undefined,
          },
          { headers: this.naglowki() },
        );
        this.dialog = false;
        await this.pobierz();
      } catch (e) {
        this.blad =
          (e.response && e.response.data && e.response.data.message) ||
          'Nie udało się zapisać licencji.';
      } finally {
        this.zapisywanie = false;
      }
    },
    async usun(item) {
      const potwierdzenie = await Swal.fire({
        icon: 'warning',
        title: 'Usunąć licencję?',
        text: `${item.city} — ${item.licenseNumber}. Kreator przestanie ją podstawiać.`,
        showCancelButton: true,
        confirmButtonText: 'Usuń',
        cancelButtonText: 'Anuluj',
      });
      if (!potwierdzenie.isConfirmed) return;
      try {
        await axios.delete(`${API}company-licenses/${item.id}`, {
          headers: this.naglowki(),
        });
        await this.pobierz();
      } catch (e) {
        Swal.fire('Nie udało się usunąć licencji', '', 'error');
      }
    },
  },
};
</script>

<style scoped>
.page-header { display: flex; align-items: flex-start; justify-content: space-between; gap: 16px; margin-bottom: 20px; }
.page-title { font-size: 22px; font-weight: 700; color: #fff; margin: 0; }
.page-subtitle { color: #6E7691; font-size: 13px; margin: 6px 0 0; max-width: 620px; }
.table-section-title { display: flex; align-items: center; gap: 10px; padding: 16px 16px 0; }
.filter-title { color: #fff; font-weight: 600; }
.search-row { padding: 12px 16px; }
.search-input { display: flex; align-items: center; gap: 8px; background: #181C28; border-radius: 12px; padding: 8px 12px; max-width: 360px; }
.search-input input { background: transparent; border: none; outline: none; color: #fff; width: 100%; }
.bezterminowa { color: #6E7691; font-style: italic; }
.licenses-page >>> .v-text-field--outlined .v-input__slot, .licenses-page >>> .v-select--outlined .v-input__slot { background: #181C28 !important; border-radius: 12px !important; }
.licenses-page >>> .v-label { color: #6E7691 !important; }
.licenses-page >>> .v-text-field input, .licenses-page >>> .v-select__selection, .licenses-page >>> textarea { color: #fff !important; }
.licenses-page >>> .v-data-table { background: #232838 !important; border: 1px solid rgba(255,255,255,0.06) !important; border-radius: 18px !important; color: #fff !important; box-shadow: none !important; }
.licenses-page >>> .v-data-table thead tr th { background: #1F2434 !important; color: #6E7691 !important; font-size: 11px !important; font-weight: 700 !important; text-transform: uppercase; letter-spacing: 0.04em; border-bottom: 1px solid rgba(255,255,255,0.06) !important; }
.licenses-page >>> .v-data-table tbody tr { background: #232838 !important; }
.licenses-page >>> .v-data-table tbody tr:hover { background: #2A3042 !important; }
.licenses-page >>> .v-data-table tbody tr td { color: #fff !important; border-bottom: 1px solid rgba(255,255,255,0.04) !important; }
.licenses-page >>> .v-data-footer { background: #1F2434 !important; color: #B0BEC5 !important; border-top: 1px solid rgba(255,255,255,0.06) !important; }
.licenses-page >>> .v-data-table tbody td .v-btn.warning { background: rgba(255,152,0,0.18) !important; color: #FFB74D !important; border-radius: 8px !important; box-shadow: none !important; min-width: 36px !important; }
.licenses-page >>> .v-data-table tbody td .v-btn.error { background: rgba(244,67,54,0.18) !important; color: #FF8A80 !important; border-radius: 8px !important; box-shadow: none !important; min-width: 36px !important; }
.licenses-page >>> .v-data-table tbody td .v-btn .v-icon { color: inherit !important; }
.licenses-page >>> .v-dialog .v-card { background: #232838 !important; }
.action-pill { display: inline-flex; align-items: center; gap: 6px; border: none; border-radius: 999px; padding: 9px 16px; font-weight: 600; font-size: 13px; cursor: pointer; }
.action-pill--primary { background: #5478FF; color: #fff; }
</style>
